import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import PaymentLinkTable from "../../payment-link-system/PaymentLinkTable";
import "../../../styles/fitness-profile/health-tracking.css";
import TableSkeleton from "../../common-components/TableSkeleton";
import { convertStraight, formatDateTime } from "../../common-data/Converts";

const HealthTrackingList = ({ isPage, setIsPage, pageLimit, setPageLimit }) => {
  const { healthTrackingLoading, healthTrackingError, healthTracking } =
    useSelector((state) => state.prospects);

  const iniHTDatas = {
    pagination: [],
    allData: [],
  };
  const [allDatas, setAllDatas] = useState(iniHTDatas);

  const getStatusBadge = (status) => {
    const statusConfig = {
      done: { bg: "#B4E7B9", color: "#0A701B" },
      not_done: { bg: "#F3DADA", color: "#D90000" },
      pending: { bg: "#FFF4E5", color: "#FF8C00" },
      active: { bg: "#B4E7B9", color: "#0A701B" },
    };

    const config = statusConfig[status] || statusConfig.not_done;

    return (
      <span
        className="tb-data healthTracking__statusBadge"
        style={{
          background: config.bg,
          color: config.color,
        }}
      >
        {status?.replace(/_/g, " ") || "-"}
      </span>
    );
  };

  const getMilestoneBadge = (milestone) => {
    const milestoneConfig = {
      "0-7_days": { bg: "#E8F5E9", color: "#2E7D32" },
      "7-15_days": { bg: "#FFF3E0", color: "#EF6C00" },
      "15-30_days": { bg: "#FFE0B2", color: "#E65100" },
      ">30_days": { bg: "#FFCDD2", color: "#C62828" },
      on_track: { bg: "#B4E7B9", color: "#0A701B" },
    };

    const config = milestoneConfig[milestone] || {
      bg: "#F5F5F5",
      color: "#616161",
    };

    return (
      <span
        className="tb-data healthTracking__milestoneBadge"
        style={{
          background: config.bg,
          color: config.color,
        }}
      >
        {milestone?.replace(/_/g, " ") || "-"}
      </span>
    );
  };

  const getPlanStatusBadge = (planStatus) => {
    const statusConfig = {
      active: { bg: "#B4E7B9", color: "#0A701B" },
      inactive: { bg: "#F3DADA", color: "#D90000" },
      expired: { bg: "#E0E0E0", color: "#424242" },
    };

    const config = statusConfig[planStatus] || statusConfig.inactive;

    return (
      <span
        className="tb-data healthTracking__planStatusBadge"
        style={{
          background: config.bg,
          color: config.color,
        }}
      >
        {planStatus || "-"}
      </span>
    );
  };

  useEffect(() => {
    if (
      !healthTrackingLoading &&
      !healthTrackingError &&
      healthTracking?.status &&
      healthTracking?.data?.length > 0
    ) {
      setAllDatas((prevs) => ({
        ...prevs,
        allData: healthTracking.data,
        pagination:
          healthTracking?.pagination?.length > 0
            ? healthTracking.pagination
            : [],
      }));
    } else {
      setAllDatas(iniHTDatas);
    }
  }, [healthTrackingLoading, healthTrackingError, healthTracking]);

  const tbColumns = [
    {
      dataField: "name",
      text: "Member Name",
      formatter: (_, row) => {
        return (
          <span className="tb-data minWidth150">
            {row?.user_name || "NA"}
            <br />
            <small className="lowercase underline">
              {row?.user_mobile || ""}
              <br />
              {row?.user_email || ""}
            </small>
          </span>
        );
      },
    },
    {
      dataField: "gym_id",
      text: "Gym Name",
      formatter: (_, row) => {
        return (
          <span className="tb-data minWidth100">{row?.gym_name || "NA"}</span>
        );
      },
    },
    {
      dataField: "dob",
      text: "DOB",
      formatter: (_, row) => {
        return (
          <span className="tb-data minWidth100">
            {row?.dob ? convertStraight(row.dob) : "-"}
          </span>
        );
      },
    },
    {
      dataField: "gender",
      text: "gender",
      formatter: (_, row) => {
        return <span className="tb-data">{row?.gender || "-"}</span>;
      },
    },
    {
      dataField: "plan_status",
      text: "plan status",
      formatter: (_, row) => {
        return getPlanStatusBadge(row?.plan_status);
      },
    },
    {
      dataField: "subscription_start_date",
      text: "subscription start",
      formatter: (_, row) => {
        return (
          <span className="tb-data minWidth100">
            {row?.subscription_start_date
              ? formatDateTime(row.subscription_start_date)
              : "NA"}
          </span>
        );
      },
    },
    {
      dataField: "subscription_expire_date",
      text: "subscription expiry",
      formatter: (_, row) => {
        return (
          <span className="tb-data minWidth100">
            {row?.subscription_expire_date
              ? formatDateTime(row.subscription_expire_date)
              : "NA"}
          </span>
        );
      },
    },
    {
      dataField: "profiling_last_date",
      text: "profiling last date",
      formatter: (_, row) => {
        return (
          <span className="tb-data minWidth100">
            {row?.profiling_last_date || "-"}
          </span>
        );
      },
    },
    {
      dataField: "profiling_weight",
      text: "weight (kg)",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            {row?.profiling_weight
              ? parseFloat(row.profiling_weight).toFixed(2)
              : 0}
          </span>
        );
      },
    },
    {
      dataField: "profiling_bmi",
      text: "BMI",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            {row?.profiling_bmi ? parseFloat(row.profiling_bmi).toFixed(1) : 0}
          </span>
        );
      },
    },
    {
      dataField: "profiling_target_weight",
      text: "target weight (kg)",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            {row?.profiling_target_weight
              ? parseFloat(row.profiling_target_weight).toFixed(2)
              : 0}
          </span>
        );
      },
    },
    {
      dataField: "profiling_status",
      text: "profiling status",
      formatter: (_, row) => {
        return getStatusBadge(row?.profiling_status);
      },
    },
    {
      dataField: "profiling_overdue_days",
      text: "profiling overdue",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            {row?.profiling_overdue_days
              ? `${row.profiling_overdue_days} days`
              : "0 days"}
          </span>
        );
      },
    },
    {
      dataField: "profiling_milestone",
      text: "profiling milestone",
      formatter: (_, row) => {
        return getMilestoneBadge(row?.profiling_milestone);
      },
    },
    {
      dataField: "bca_last_date",
      text: "BCA last date",
      formatter: (_, row) => {
        return (
          <span className="tb-data minWidth100">
            {row?.bca_last_date ? formatDateTime(row.bca_last_date) : "-"}
          </span>
        );
      },
    },
    {
      dataField: "bca_status",
      text: "BCA status",
      formatter: (_, row) => {
        return getStatusBadge(row?.bca_status);
      },
    },
    {
      dataField: "bca_overdue_days",
      text: "BCA overdue",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            {row?.bca_overdue_days ? `${row.bca_overdue_days} days` : "0 days"}
          </span>
        );
      },
    },
    {
      dataField: "bca_pending_count",
      text: "BCA pending count",
      formatter: (_, row) => {
        return <span className="tb-data">{row?.bca_pending_count || "0"}</span>;
      },
    },
    {
      dataField: "trainer_id",
      text: "current trainer",
      formatter: (_, row) => {
        return (
          <span className="tb-data minWidth150">
            {row?.trainer_name || "NA"}
            <br />
            <small className="lowercase underline">
              {row?.trainer_mobile || ""}
              <br />
              {row?.trainer_email || ""}
            </small>
          </span>
        );
      },
    },
    {
      dataField: "trainer_status",
      text: "trainer status",
      formatter: (_, row) => {
        return getStatusBadge(row?.trainer_status);
      },
    },
  ];

  return (
    <div className="fullWidthFlexCenter overflowAuto separatedTable no-border-table mt30">
      {healthTrackingLoading ? (
        <TableSkeleton />
      ) : (
        <PaymentLinkTable
          tableData={allDatas.allData}
          tableColumns={tbColumns}
          tablePagination={allDatas.pagination}
          isPage={isPage}
          setIsPage={(value) => setIsPage(value)}
          pageLimit={pageLimit}
          setPageLimit={setPageLimit}
        />
      )}
    </div>
  );
};

export default HealthTrackingList;
